/**
 * Card del richiedente nel pannello Contesto (prototipo `Centro Ticket.dc.html`
 * L308-L332).
 *
 * Parita con la card attuale di `SupportCenterV2.tsx:388-470`: badge tipo,
 * badge origine, chip dei tag, nome, email, telefono, codice, citta ·
 * provincia, link "Scheda associato", hint "l'email coincide con un associato"
 * (mai collegamento automatico) e bottone "Rigenera link ospite" con
 * ConfirmDialog e toast.
 *
 * Esiste SOLO nel Centro Ticket admin: nell'area rappresentante il richiedente
 * non ha mai avuto una card dedicata, e `api.regenerateGuestToken` non e
 * implementata dal suo adapter.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BadgeCheck, ExternalLink, Link2, Mail, Phone } from 'lucide-react';
import { useToast } from '../../../contexts/ToastContext';
import ConfirmDialog from '../../ConfirmDialog';
import { S3Avatar, S3Badge } from './primitives';
import { originVisual, requesterTypeVisual } from './visual';
import { formatDate, initialsOf } from '../constants';
import { photoUrl } from '../../../utils/photoUrl';
import type { OperatorSupportApi, TicketDetail } from '../../../services/supportApi';

export interface RequesterCardProps {
  ticket: TicketDetail;
  api: OperatorSupportApi;
  canHandle: boolean;
  onChanged: () => void | Promise<void>;
}

const RequesterCard: React.FC<RequesterCardProps> = ({ ticket, api, canHandle, onChanged }) => {
  const toast = useToast();
  const [confirmRegen, setConfirmRegen] = useState(false);
  const [busy, setBusy] = useState(false);

  const req = ticket.requester;
  const snap = ticket.associateSnapshot || {};
  // Senza `requester` il ticket e nato da un flusso interno all'area
  // riservata: il richiedente e un associato (default di `SupportCenterV2.tsx:395`).
  const type = requesterTypeVisual(req?.type || 'associate');
  const or = originVisual(ticket.origin);
  const nome = req?.nome || snap.nominativo || '—';
  const email = req?.email || snap.email || '';
  // Stessa risoluzione della card attuale (SupportCenterV2.tsx:392): lo
  // snapshot vale quanto il riferimento sul richiedente.
  const associateId = req?.associateId || snap._id;
  const citta = [snap.citta, snap.provincia].filter(Boolean).join(' · ');
  // Ultimo cambio del richiedente (Task 23/C): chi legge la card deve sapere
  // che il ticket non e nato a nome di questa persona.
  const lastChange = [...(ticket.history || [])].reverse().find((e) => e.type === 'requester_changed');

  const regenerate = async () => {
    setConfirmRegen(false);
    setBusy(true);
    try {
      await api.regenerateGuestToken?.(ticket._id);
      toast.success('Link ospite rigenerato: il precedente non è più valido');
      await onChanged();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Errore rigenerazione link ospite');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="s3-card s3-req" aria-busy={busy || undefined}>
      <div className="s3-req__badges">
        {type.label && <S3Badge tone={type.tone} small>{type.label}</S3Badge>}
        {or.label && <S3Badge tone={or.tone} small>{or.label}</S3Badge>}
        {snap.codice && (
          <S3Badge tone="chip" small title="Codice associato">Codice {snap.codice}</S3Badge>
        )}
      </div>

      <div className="s3-req__who">
        {/* Foto dell'associato quando c'e (`requester.foto`, vista operatore): stesso
            cerchio, stessa misura; senza foto restano le iniziali. */}
        <S3Avatar initials={initialsOf(nome)} size={38} title={nome} src={photoUrl(req?.foto)} />
        <div className="s3-req__names">
          <div className="s3-req__nome" title={nome}>{nome}</div>
          {citta && <div className="s3-req__citta">{citta}</div>}
        </div>
      </div>

      {lastChange && (
        <div className="s3-meta" title={lastChange.byName ? `Cambiato da ${lastChange.byName}` : undefined}>
          Richiedente cambiato il {formatDate(lastChange.at)}
        </div>
      )}

      {(email || req?.telefono) && (
        <div className="s3-req__contacts">
          {email && (
            <a href={`mailto:${email}`} className="s3-req__contact">
              <Mail size={12} aria-hidden="true" />
              <span className="s3-ellipsis" title={email}>{email}</span>
            </a>
          )}
          {req?.telefono && (
            <a href={`tel:${req.telefono}`} className="s3-req__contact">
              <Phone size={12} aria-hidden="true" /> {req.telefono}
            </a>
          )}
        </div>
      )}

      {associateId && (
        <Link to={`/admin/associates/${associateId}`} className="s3-link s3-req__link">
          <ExternalLink size={12} aria-hidden="true" /> Scheda associato
        </Link>
      )}

      {/* Hint: email uguale a quella di un associato. Mai collegamento
          automatico (parita SupportCenterV2.tsx:438-457). */}
      {!associateId && req?.possibleAssociateId && (
        <div className="s3-req__hint">
          <span className="s3-req__hint-row">
            <BadgeCheck size={12} aria-hidden="true" /> L’email coincide con quella di un associato.
          </span>{' '}
          <Link to={`/admin/associates/${req.possibleAssociateId}`} className="s3-link">
            Apri la scheda
          </Link>
        </div>
      )}

      {canHandle && req?.type === 'guest' && api.regenerateGuestToken && (
        <button
          type="button"
          className="s3-pill s3-pill--sm s3-req__regen"
          disabled={busy}
          onClick={() => setConfirmRegen(true)}
        >
          <Link2 size={12} aria-hidden="true" /> Rigenera link ospite
        </button>
      )}

      <ConfirmDialog
        isOpen={confirmRegen}
        onClose={() => setConfirmRegen(false)}
        onConfirm={regenerate}
        title="Rigenera link ospite"
        message="Il link inviato in precedenza smetterà di funzionare e ne verrà generato uno nuovo. Procedere?"
        confirmText="Rigenera"
        type="warning"
      />
    </div>
  );
};

export default RequesterCard;
